import { ProductType } from '@tink/core/src/types';
import { assign, createMachine } from 'xstate';
import { ErrorReason, ErrorStatus } from '@tink/core/src/tinkLinkErrorCodes';
import {
  CredentialsService,
  credentialsService as credentialsServiceImport,
} from '../services/credentialsService';
import { CreateUrlHandoffSessionRequest } from '../../lib-api/services/HandoffSession';
import { Credentials, CredentialsEntity } from '../../models/credentials';
import {
  ConsumedHandoffSession,
  HandoffCredentialsAction,
  HandoffSession,
} from '../../models/handoffSession';
import { ProductDimension } from '../../models/product';
import { ProviderEntity } from '../../models/provider';
import { routeBuilder } from '../../routes/routeBuilder';
import { ApplicationErrorPayload } from '../../stores/app-state-store';
import { ERR_USER_CANCELLED, FETCHING_CREDENTIALS_FAILED } from '../../utilities/error';
import {
  ERR_HANDOFF_CREATE_SESSION,
  ERR_HANDOFF_SESSION_FAILED_GET_SESSION,
  HandoffSessionService,
  handoffSessionService as handoffSessionServiceImport,
} from '../services/handoffSessionService';
import { createCredentialsPollerMachine as createCredentialsPollerMachineImport } from './credentialsPollerMachine';
import { createHandoffSessionPollerMachine as createHandoffSessionPollerMachineImport } from './handoffSessionPollerMachine';

export type HandoffCreatorMachineContext = {
  handoffSession: ConsumedHandoffSession | HandoffSession | undefined;
  credentials?: CredentialsEntity;
  error?: ApplicationErrorPayload;
};

type HandoffCreatorMachineServices = {
  createUrlHandoffSession: {
    data: HandoffSession;
  };
  getCredentials: {
    data: CredentialsEntity;
  };
  pollHandoffSession: any;
  pollCredentials: any;
};

export type HandoffCreatorMachineEvents = {
  type: 'EVENT_CANCEL_CONSUMED_HANDOFF_SESSION';
  fields: undefined;
};

export type HandoffCreatorMachineParams = {
  provider: ProviderEntity;
  version: string;
  productType: ProductType;
  productDimension: ProductDimension;
  trackingId: string;
  userAccessToken?: string;
  userHintId?: string;
  handoffCredentialsAction: HandoffCredentialsAction;
  createHandoffSessionPollerMachine?: typeof createHandoffSessionPollerMachineImport;
  createCredentialsPollerMachine?: typeof createCredentialsPollerMachineImport;
  handoffSessionService?: HandoffSessionService;
  credentialsService?: CredentialsService;
  initialContext?: HandoffCreatorMachineContext;
  initialState?: string;
};

export const USER_ACCESS_TOKEN_UNDEFINED_ERROR = {
  errorStatus: ErrorStatus.AUTHENTICATION_ERROR,
  errorReason: ErrorReason.INVALID_STATE_ACCESS_TOKEN,
};

export const INTERNAL_UNEXPECTED_ERROR = {
  errorStatus: ErrorStatus.INTERNAL_ERROR,
  errorReason: ErrorReason.UNEXPECTED_HANDOFF_ERROR,
};

export const createHandoffCreatorMachine = ({
  userAccessToken,
  provider,
  version,
  productType,
  productDimension,
  trackingId,
  handoffCredentialsAction,
  createHandoffSessionPollerMachine = createHandoffSessionPollerMachineImport,
  createCredentialsPollerMachine = createCredentialsPollerMachineImport,
  handoffSessionService = handoffSessionServiceImport,
  credentialsService = credentialsServiceImport,
  initialContext,
  initialState = 'decideIfStartHandoffSession',
}: HandoffCreatorMachineParams) =>
  createMachine(
    {
      context: initialContext || {
        handoffSession: undefined,
        credentials: undefined,
      },
      tsTypes: {} as import('./handoffCreatorMachine.typegen').Typegen0,
      schema: {
        context: {} as HandoffCreatorMachineContext,
        services: {} as HandoffCreatorMachineServices,
        events: {} as HandoffCreatorMachineEvents,
      },
      predictableActionArguments: true,
      id: 'handoff-creator-machine',
      initial: initialState,
      states: {
        decideIfStartHandoffSession: {
          description: 'Create HandoffSession if accessToken is available',
          always: [
            {
              target: 'processCreateUrlHandoffSession',
              cond: 'hasUserAccessToken',
            },
            {
              target: 'processError',
              actions: 'actionUserAccessTokenError',
            },
          ],
        },
        processCreateUrlHandoffSession: {
          invoke: {
            src: 'createUrlHandoffSession',
            onDone: [
              {
                target: 'decideHandoffSessionHandling',
                actions: 'actionPersistHandoffSession',
              },
            ],
            onError: [
              {
                target: 'processError',
                actions: 'actionCreateHandoffError',
              },
            ],
          },
        },
        decideHandoffSessionHandling: {
          description: 'Handling HandoffSession after polling',
          on: {
            EVENT_CANCEL_CONSUMED_HANDOFF_SESSION: {
              target: 'processError',
              actions: 'actionUserCancelledError',
            },
          },
          always: [
            {
              cond: 'isHandoffSessionCredentialsAvailable',
              target: 'processGetCredentials',
            },
            {
              cond: 'isHandoffSessionAvailable',
              target: 'uiPollingHandoffSession',
            },
            {
              target: 'processError',
              actions: 'actionUnexpectedError',
            },
          ],
        },
        processGetCredentials: {
          on: {
            EVENT_CANCEL_CONSUMED_HANDOFF_SESSION: {
              target: 'processError',
              actions: 'actionUserCancelledError',
            },
          },
          invoke: {
            src: 'getCredentials',
            onDone: [
              {
                target: 'decideCredentialsHandling',
                actions: 'actionPersistCredentials',
              },
            ],
            onError: [
              {
                target: 'processError',
                actions: 'actionGetCredentialsError',
              },
            ],
          },
        },
        uiPollingHandoffSession: {
          description: 'Poller for getting the HandoffSession',
          on: {
            EVENT_CANCEL_CONSUMED_HANDOFF_SESSION: {
              target: 'processError',
              actions: 'actionUserCancelledError',
            },
          },
          invoke: {
            src: 'pollHandoffSession',
            onDone: [
              {
                target: 'decideHandoffSessionHandling',
                actions: 'actionPersistHandoffSession',
              },
            ],
            onError: [
              {
                target: 'processError',
                actions: 'actionGetHandoffError',
              },
            ],
          },
        },
        decideCredentialsHandling: {
          description: 'Handling credentials after polling',
          on: {
            EVENT_CANCEL_CONSUMED_HANDOFF_SESSION: {
              target: 'processError',
              actions: 'actionUserCancelledError',
            },
          },
          always: [
            {
              cond: 'isCredentialsUpdated',
              target: 'processCredentialsUpdated',
            },
            {
              cond: 'isCredentialsError',
              target: 'processCredentialsError',
            },
            {
              cond: 'hasCredentials',
              target: 'uiPollingHandoffCredentials',
            },
            {
              target: 'processError',
              actions: 'actionUnexpectedError',
            },
          ],
        },
        uiPollingHandoffCredentials: {
          description: 'Poller for getting the CredentialsEntity',
          on: {
            EVENT_CANCEL_CONSUMED_HANDOFF_SESSION: {
              target: 'processError',
              actions: 'actionUserCancelledError',
            },
          },
          invoke: {
            src: 'pollCredentials',
            onDone: [
              {
                target: 'decideCredentialsHandling',
                actions: 'actionPersistCredentials',
              },
            ],
            onError: [
              {
                target: 'processError',
                actions: 'actionGetCredentialsError',
              },
            ],
          },
        },
        processCredentialsUpdated: {
          type: 'final',
        },
        processCredentialsError: {
          type: 'final',
          description:
            'These errors will use the onDone callback in the view to propagate the errors thorugh redux',
        },
        processError: {
          type: 'final',
          description: 'These errors will use the onError callback in the view',
        },
      },
    },
    {
      services: {
        createUrlHandoffSession: async () => {
          const urlToMinimize = routeBuilder.buildHandoffUrlToMinimize.build({
            version,
            productType,
            productDimension,
            authorizationToken: userAccessToken!,
            handoffCredentialsAction,
            trackingId,
            market: provider.market,
          });
          const createUrlHandoffSessionRequest: CreateUrlHandoffSessionRequest = {
            providerName: provider.name,
            userAccessToken: userAccessToken!,
            sessionId: trackingId,
            url: urlToMinimize,
          };

          const result = await handoffSessionService.createUrlHandoffSession(
            createUrlHandoffSessionRequest
          );

          return result;
        },
        pollHandoffSession: ({ handoffSession }) =>
          createHandoffSessionPollerMachine({
            handoffSessionId: handoffSession?.id ? handoffSession?.id : '',
          }),
        getCredentials: ({ handoffSession }) =>
          credentialsService.getCredentials(
            handoffSession?.credentialsId ? handoffSession?.credentialsId : ''
          ),
        pollCredentials: ({ credentials }) =>
          createCredentialsPollerMachine({ credentials: credentials! }),
      },
      guards: {
        hasUserAccessToken: () => Boolean(userAccessToken),
        isHandoffSessionAvailable: ({ handoffSession }) => Boolean(handoffSession),
        isHandoffSessionCredentialsAvailable: ({ handoffSession }) =>
          Boolean(handoffSession?.credentialsId && handoffSession?.credentialsId.length !== 0),
        hasCredentials: ({ credentials }) => Boolean(credentials),
        isCredentialsError: ({ credentials }) => Credentials.hasError(credentials),
        isCredentialsUpdated: ({ credentials }) => Credentials.isUpdated(credentials),
      },
      actions: {
        actionPersistHandoffSession: assign({
          handoffSession: (_, event) => event.data as HandoffSession,
        }),
        actionPersistCredentials: assign({
          credentials: (_, event) => event.data as CredentialsEntity,
        }),
        actionUserAccessTokenError: assign({
          error: (_) => USER_ACCESS_TOKEN_UNDEFINED_ERROR as ApplicationErrorPayload,
        }),
        actionCreateHandoffError: assign({
          error: (_) => ERR_HANDOFF_CREATE_SESSION as ApplicationErrorPayload,
        }),
        actionGetHandoffError: assign({
          error: (_) => ERR_HANDOFF_SESSION_FAILED_GET_SESSION as ApplicationErrorPayload,
        }),
        actionUserCancelledError: assign({
          error: (_) => ERR_USER_CANCELLED as ApplicationErrorPayload,
        }),
        actionGetCredentialsError: assign({
          error: (_) => FETCHING_CREDENTIALS_FAILED as ApplicationErrorPayload,
        }),
        actionUnexpectedError: assign({
          error: (_) => INTERNAL_UNEXPECTED_ERROR as ApplicationErrorPayload,
        }),
      },
    }
  );
